// imagepasteResize — pure geometry for the imagepaste preview resize UI
// (corner handles + 25/50/100/200% buttons). All coordinates are canvas (= layer)
// coordinates; the caller converts pointer client px via `CanvasViewMapper`.
//
// Resizing is always aspect-locked to the ORIGINAL image ratio: a single scale
// factor `s` (size = natural * s) is the only degree of freedom, so the width and
// height can never drift apart.

export interface Size {
  readonly w: number;
  readonly h: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export type Corner = 'nw' | 'ne' | 'sw' | 'se';

/** Minimum length (px) of the long side. Smaller originals use their own size as the floor. */
export const MIN_LONG_SIDE_PX = 16;

export const SCALE_PRESETS_PERCENT = [25, 50, 100, 200] as const;

export interface ScaleLimits {
  readonly min: number;
  readonly max: number;
}

/**
 * Allowed scale range. min: long side >= 16px (or the natural size when the
 * original is already smaller). max: the largest size that still fits the canvas.
 */
export const computeScaleLimits = (natural: Size, canvas: Size): ScaleLimits => {
  const long = Math.max(natural.w, natural.h);
  const fit = Math.min(canvas.w / natural.w, canvas.h / natural.h);
  const max = Math.max(fit, 0);
  const min = Math.min(1, MIN_LONG_SIDE_PX / long, max > 0 ? max : 1);
  return { min, max: Math.max(max, min) };
};

export const clampScale = (s: number, limits: ScaleLimits): number =>
  Math.max(limits.min, Math.min(limits.max, s));

export const sizeFromScale = (natural: Size, s: number): Size => ({
  w: Math.max(1, Math.round(natural.w * s)),
  h: Math.max(1, Math.round(natural.h * s)),
});

/** Current scale of `width` relative to the natural width (1 = 100%). */
export const currentScale = (width: number, natural: Size): number => width / natural.w;

export const percentLabel = (width: number, natural: Size): string =>
  `${Math.round(currentScale(width, natural) * 100)}%`;

export const cornerPoint = (rect: Rect, corner: Corner): { x: number; y: number } => ({
  x: corner === 'nw' || corner === 'sw' ? rect.x : rect.x + rect.w,
  y: corner === 'nw' || corner === 'ne' ? rect.y : rect.y + rect.h,
});

export const oppositeCorner = (corner: Corner): Corner =>
  corner === 'nw' ? 'se' : corner === 'ne' ? 'sw' : corner === 'sw' ? 'ne' : 'nw';

/**
 * Resize by dragging `corner` to `pointer` (canvas coords, already corrected for
 * the grab offset). The diagonally opposite corner of `start` stays fixed. The
 * larger of the two axis requests wins. Growth is capped so the moving corner
 * stays inside the canvas, but never below the starting scale (an image that was
 * already partly off-canvas does not snap smaller on grab).
 */
export const resizeFromCorner = (input: {
  readonly start: Rect;
  readonly corner: Corner;
  readonly pointer: { x: number; y: number };
  readonly natural: Size;
  readonly canvas: Size;
}): Rect => {
  const { start, corner, pointer, natural, canvas } = input;
  const fixed = cornerPoint(start, oppositeCorner(corner));
  const sx = corner === 'ne' || corner === 'se' ? 1 : -1;
  const sy = corner === 'sw' || corner === 'se' ? 1 : -1;
  const limits = computeScaleLimits(natural, canvas);
  const requested = Math.max(
    ((pointer.x - fixed.x) * sx) / natural.w,
    ((pointer.y - fixed.y) * sy) / natural.h,
  );
  const roomX = sx > 0 ? canvas.w - fixed.x : fixed.x;
  const roomY = sy > 0 ? canvas.h - fixed.y : fixed.y;
  const startScale = currentScale(start.w, natural);
  const cap = Math.max(Math.min(limits.max, roomX / natural.w, roomY / natural.h), startScale);
  const s = clampScale(Math.min(requested, cap), limits);
  const { w, h } = sizeFromScale(natural, s);
  return { x: sx > 0 ? fixed.x : fixed.x - w, y: sy > 0 ? fixed.y : fixed.y - h, w, h };
};

/**
 * Rescale to `percent` of the natural size keeping the rect centre fixed, clamped
 * to the scale limits and then shifted so the result stays inside the canvas.
 */
export const resizeToPercent = (input: {
  readonly rect: Rect;
  readonly percent: number;
  readonly natural: Size;
  readonly canvas: Size;
}): Rect => {
  const { rect, percent, natural, canvas } = input;
  const limits = computeScaleLimits(natural, canvas);
  const { w, h } = sizeFromScale(natural, clampScale(percent / 100, limits));
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const x = Math.round(Math.max(0, Math.min(canvas.w - w, cx - w / 2)));
  const y = Math.round(Math.max(0, Math.min(canvas.h - h, cy - h / 2)));
  return { x, y, w, h };
};
